import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import CloudBand from '../../components/common/CloudBand';
import WanderWordsLogo from '../../components/common/WanderWordsLogo';
import OnboardingPageCard from '../../components/common/OnboardingPageCard';
import ToolTipLing from '../../components/common/ToolTipLing';
import { createLearner, updateLearner } from '../../api/learners';
import { updateUserProfile } from '../../api/users';
import { useLearnerStore } from '../../stores/learnerStore';
import { useLearner } from '../../hooks/useLearner';
import s from './OnboardingPages.module.css';

const AGES = [5, 6, 7, 8, 9, 10, 11, 12];
const STEP_COUNT = 5;

export default function ChildProfilePage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();
  const location = useLocation();
  const isAddingChild = location.state?.isAddingChild ?? false;

  const [parentName, setParentName] = useState('');
  const [childName, setChildName] = useState('');
  const [age, setAge] = useState('');

  const queryClient = useQueryClient();
  const { data: activeLearner } = useLearner();
  const setActiveLearnerId = useLearnerStore((state) => state.setActiveLearnerId);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentStep = 1;
  // When adding a child (parent already exists), parent name field is hidden
  const canSubmit = (isAddingChild || parentName.trim()) && childName.trim() && age;

  async function handleContinue() {
    if (!canSubmit || isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      // Draft-first: add-child and first-time flows share the same path.
      // isAddingChild only affects UI (hides parentName field + swaps the
      // primary button for a cancel/continue pair).
      let learner;
      const existingDraft = activeLearner?.status === 'draft' ? activeLearner : null;

      if (existingDraft?.id) {
        // Resume: update the existing draft
        learner = await updateLearner(existingDraft.id, {
          name: childName.trim(),
          age: Number(age),
          onboarding_step: 1,
        });
      } else {
        // New: create a draft
        try {
          learner = await createLearner({
            name: childName.trim(),
            age: Number(age),
            status: 'draft',
            onboarding_step: 1,
          });
        } catch (err) {
          // 409 = a draft already exists — fall through to update flow
          if (err?.response?.status === 409 || err?.statusCode === 409 || err?.status === 409) {
            const draft = err?.response?.data?.draft || err?.draft;
            if (draft?.id) {
              learner = await updateLearner(draft.id, {
                name: childName.trim(),
                age: Number(age),
                onboarding_step: 1,
              });
            } else {
              throw err;
            }
          } else {
            throw err;
          }
        }
      }

      // FP-LEARNER-STORE-RETIREMENT-01: optimistic patch + invalidate so the
      // next page reads the new draft from React Query.
      queryClient.setQueryData(['learners'], (prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const exists = list.some((l) => l.id === learner.id);
        return exists
          ? list.map((l) => (l.id === learner.id ? { ...l, ...learner } : l))
          : [...list, learner];
      });
      setActiveLearnerId(learner.id);
      queryClient.invalidateQueries({ queryKey: ['learners'] });

      // Save parent name (non-critical)
      if (parentName.trim()) {
        try {
          await updateUserProfile({ name: parentName.trim() });
          queryClient.invalidateQueries({ queryKey: ['me'] });
        } catch { /* non-critical */ }
      }

      navigate('/onboarding/buddy');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    handleContinue();
  }

  return (
    <main
      className={s.page}
      style={{ '--cloud-color': '#2AB8A8', '--content-gap': '54px', '--row-gap': '83px' }}
      ref={mainRef}
      tabIndex={-1}
    >
      {/* DECORATION */}
      <div className={`${s.topBand} ${s.topBandTeal}`} />
      <CloudBand variant="teal" className={s.cloudBandTeal} />

      {/* Desktop logo */}
      <WanderWordsLogo className={s.logo} />

      {/* CONTENT */}
      <div className={`${s.content} ${s.mobileOnly}`}>
        <div className={s.contentRow}>
          {/* Left column — teal section (step counter + heading + mascot) */}
          <div className={`${s.leftCol} ${s.leftColTeal}`}>
            {/* Step counter */}
            <div className={s.stepCounter}>
              {Array.from({ length: STEP_COUNT }, (_, i) => {
                const step = i + 1;
                const isActive = step <= currentStep;
                return (
                  <div
                    key={step}
                    className={`${s.stepDot} ${isActive ? s.stepDotActive : ''}`}
                    aria-label={`Step ${step} of ${STEP_COUNT}`}
                  >
                    {step}
                  </div>
                );
              })}
            </div>

            {/* Header: Title + Ling mascot (mobile only — desktop uses card) */}
            <div className={`${s.headerRow} ${s.headerRowTypeA}`}>
              <div className={s.headerColumn}>
                <h1 className={s.pageTitle}>{t('childProfile.title')}</h1>
                <p className={`${s.subtitle} ${s.subtitleDark}`}>{t('childProfile.subtitle')}</p>
              </div>
              <img
                src="/assets/ling-onboarding.png"
                alt="Ling the cat"
                className={s.mascotImg}
              />
            </div>
          </div>
        </div>

        {/* Cream bottom section — form + button area */}
        <div className={s.mobileCreamSection}>
          <div className={s.rightCol}>
            <form
              id="child-profile-form"
              onSubmit={handleSubmit}
              className={s.childProfileForm}
            >
              {error && (
                <div className={s.errorBanner} role="alert">{error}</div>
              )}
              {/* Parent's first name — hidden when adding a child (parent already registered) */}
              {!isAddingChild && (
                <div className={s.formField}>
                  <label className={s.formLabel} htmlFor="parent-name">
                    {t('childProfile.parentName')}
                  </label>
                  <input
                    id="parent-name"
                    type="text"
                    placeholder={t('childProfile.parentNamePlaceholder')}
                    value={parentName}
                    onChange={(e) => setParentName(e.target.value)}
                    required={!isAddingChild}
                    autoComplete="given-name"
                    className={s.formInput}
                  />
                </div>
              )}

              {/* Child's first name */}
              <div className={s.formField}>
                <label className={s.formLabel} htmlFor="child-name">
                  {t('childProfile.childName')}
                </label>
                <input
                  id="child-name"
                  type="text"
                  placeholder={t('childProfile.childNamePlaceholder')}
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  required
                  className={s.formInput}
                />
              </div>

              {/* Age */}
              <div className={s.formField}>
                <label className={s.formLabel} htmlFor="child-age">
                  {t('childProfile.age')}
                </label>
                <select
                  id="child-age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  required
                  className={s.formSelect}
                >
                  <option value="" disabled>{t('childProfile.agePlaceholder')}</option>
                  {AGES.map((a) => (
                    <option key={a} value={a}>
                      {t('childProfile.ageDisplay', { age: a })}
                    </option>
                  ))}
                </select>
              </div>

              {/* Helper text */}
              <p className={s.helperText}>
                {t('childProfile.ageExplain', {
                  name: childName.trim() || t('childProfile.yourChild'),
                })}
              </p>
            </form>

            <div className={s.spacer} />
          </div>

          {/* Button area — sticky to bottom of viewport on mobile */}
          <div className={s.stickyBtnArea}>
            {isAddingChild ? (
              <div className={s.btnRow}>
                <button
                  type="button"
                  onClick={() => navigate('/settings')}
                  className={s.cancelBtn}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  form="child-profile-form"
                  disabled={!canSubmit || isLoading}
                  className={s.continueBtn}
                >
                  {isLoading ? 'Saving…' : t('common.continue')}
                </button>
              </div>
            ) : (
              <button
                type="submit"
                form="child-profile-form"
                disabled={!canSubmit || isLoading}
                className={s.continueBtn}
              >
                {isLoading ? 'Saving…' : t('common.continue')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* DESKTOP CARD — renders on top of mobile layout at ≥1200px */}
      <OnboardingPageCard
        stepper={
          <div className={s.stepCounter}>
            {Array.from({ length: STEP_COUNT }, (_, i) => {
              const step = i + 1;
              const isActive = step <= currentStep;
              return (
                <div key={step} className={`${s.stepDot} ${isActive ? s.stepDotActive : ''}`}>
                  {step}
                </div>
              );
            })}
          </div>
        }
        leftContent={
          <>
            {/* Title + subtitle */}
            <div className={s.headerColumn}>
              <h1 className={s.pageTitle}>{t('childProfile.title')}</h1>
              <p className={s.subtitle}>{t('childProfile.subtitle')}</p>
            </div>
            {/* ToolTipLing pinned to bottom */}
            <ToolTipLing type="default">
              {t('buddy.tooltipStep1')}
            </ToolTipLing>
          </>
        }
        rightContent={
          <>
            {error && (
              <div className={s.errorBanner} role="alert">{error}</div>
            )}
            {/* Form fields — same markup as mobile form, minus the <form> wrapper */}
            <div className={s.desktopFormFields}>
              {!isAddingChild && (
                <div className={s.formField}>
                  <label className={s.formLabel} htmlFor="desktop-parent-name">
                    {t('childProfile.parentName')}
                  </label>
                  <input
                    id="desktop-parent-name"
                    type="text"
                    placeholder={t('childProfile.parentNamePlaceholder')}
                    value={parentName}
                    onChange={(e) => setParentName(e.target.value)}
                    autoComplete="given-name"
                    className={s.formInput}
                  />
                </div>
              )}
              <div className={s.formField}>
                <label className={s.formLabel} htmlFor="desktop-child-name">
                  {t('childProfile.childName')}
                </label>
                <input
                  id="desktop-child-name"
                  type="text"
                  placeholder={t('childProfile.childNamePlaceholder')}
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  className={s.formInput}
                />
              </div>
              <div className={s.formField}>
                <label className={s.formLabel} htmlFor="desktop-child-age">
                  {t('childProfile.age')}
                </label>
                <select
                  id="desktop-child-age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className={s.formSelect}
                >
                  <option value="" disabled>{t('childProfile.agePlaceholder')}</option>
                  {AGES.map((a) => (
                    <option key={a} value={a}>{t('childProfile.ageDisplay', { age: a })}</option>
                  ))}
                </select>
              </div>
              <p className={s.helperText}>
                {t('childProfile.ageExplain', { name: childName.trim() || t('childProfile.yourChild') })}
              </p>
            </div>
            {/* Continue button */}
            <button
              type="button"
              onClick={handleContinue}
              disabled={!canSubmit || isLoading}
              className={s.continueBtn}
            >
              {isLoading ? 'Saving…' : t('common.continue')}
            </button>
          </>
        }
      />
    </main>
  );
}
